import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DeveloperReportsService, DeveloperProjectReport } from '../services/developer-reports.service';
import { useNotification } from '../context/NotificationContext';
import styles from './DeveloperProjectReportsPage.module.css';

export default function DeveloperProjectReportsPage() {
  const { id } = useParams<{ id: string }>();
  const { addNotification } = useNotification();
  const { i18n } = useTranslation();
  const [data, setData] = useState<DeveloperProjectReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<'pdf' | 'csv' | null>(null);
  const locale = i18n.language.startsWith('es') ? 'es-MX' : 'en-US';
  const currency = i18n.language.startsWith('es') ? 'MXN' : 'USD';

  const formatMoney = (value: number, customCurrency?: string | null) =>
    Number(value || 0).toLocaleString(locale, {
      style: 'currency',
      currency: customCurrency || currency,
      minimumFractionDigits: 0
    });

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    DeveloperReportsService.getProjectReport(id)
      .then(res => setData(res))
      .catch((error: any) => addNotification(error?.message || 'Error cargando reporte', 'error'))
      .finally(() => setLoading(false));
  }, [id, addNotification]);

  const downloadReport = async (format: 'pdf' | 'csv') => {
    if (!id) return;
    setDownloading(format);
    try {
      await DeveloperReportsService.downloadReport({ projectId: id, format });
    } catch (error: any) {
      addNotification(error?.message || 'No se pudo descargar el reporte.', 'error');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) return <div className={styles.loading}>Cargando...</div>;
  if (!data) return <div className={styles.loading}>Sin datos.</div>;

  const progress = Math.max(0, Math.min(100, Number(data.fundedPct) || 0));
  const timeline = Array.isArray(data.fundingTimeline) ? data.fundingTimeline : [];
  const investments = Array.isArray(data.investments) ? data.investments : [];
  const closures = Array.isArray(data.closures) ? data.closures : [];
  const isClosed =
    data.projectStatus === 'COMPLETED' ||
    data.projectState === 'FINISHED' ||
    closures.length > 0 ||
    data.isActive === false;

  const closureSummary =
    data.closureSummary ||
    closures.reduce(
      (acc, row) => {
        acc.totalCapital += Number(row.capital || 0);
        acc.totalYield += Number(row.yield || 0);
        acc.totalReturned += Number(row.total || 0);
        acc.ordersClosed += 1;
        return acc;
      },
      { totalCapital: 0, totalYield: 0, totalReturned: 0, ordersClosed: 0 }
    );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Reporte</p>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>Reporte de fondeo</h1>
            <span className={`${styles.statusBadge} ${isClosed ? styles.statusClosed : styles.statusOpen}`}>
              {isClosed ? 'Cierre completado' : 'Cierre pendiente'}
            </span>
          </div>
          <p className={styles.subtitle}>Resumen del desempeño de inversión del proyecto.</p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/developer/panel" className={styles.backButton}>
            Volver al panel
          </Link>
          <button
            type="button"
            className={styles.exportButton}
            onClick={() => downloadReport('pdf')}
            disabled={!!downloading}
          >
            {downloading === 'pdf' ? 'Generando...' : 'PDF'}
          </button>
          <button
            type="button"
            className={styles.exportButton}
            onClick={() => downloadReport('csv')}
            disabled={!!downloading}
          >
            {downloading === 'csv' ? 'Generando...' : 'CSV'}
          </button>
          <div className={styles.progressCard}>
            <span className={styles.progressLabel}>Avance</span>
            <span className={styles.progressValue}>{progress}%</span>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </header>

      <section className={styles.statsGrid}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Total fondeado</span>
          <span className={styles.statValue}>{formatMoney(data.fundedAmount)}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Bloques vendidos</span>
          <span className={styles.statValue}>{data.blocksSold} / {data.blocksTotal}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Inversionistas únicos</span>
          <span className={styles.statValue}>{data.investorsCount}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Porcentaje fondeado</span>
          <span className={styles.statValue}>{progress}%</span>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Resumen de cierre</h2>
          <span className={styles.cardBadge}>{closureSummary.ordersClosed} órdenes</span>
        </div>
        {closureSummary.ordersClosed === 0 ? (
          <div className={styles.emptyState}>Disponible cuando el proyecto esté finalizado.</div>
        ) : (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Capital devuelto</span>
              <span className={styles.summaryValue}>{formatMoney(closureSummary.totalCapital)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Utilidad total</span>
              <span className={styles.summaryValue}>{formatMoney(closureSummary.totalYield)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Total devuelto</span>
              <span className={styles.summaryValue}>{formatMoney(closureSummary.totalReturned)}</span>
            </div>
          </div>
        )}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Evolución de fondeo</h2>
          <span className={styles.cardBadge}>{timeline.length} registros</span>
        </div>
        {timeline.length > 0 ? (
          <ul className={styles.timeline}>
            {timeline.map(item => (
              <li key={item.date} className={styles.timelineItem}>
                <span>{formatDate(item.date)}</span>
                <strong>{formatMoney(item.amount)}</strong>
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.emptyState}>Sin movimientos aún.</div>
        )}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Inversiones recientes</h2>
          <span className={styles.cardBadge}>{investments.length} inversiones</span>
        </div>
        {investments.length === 0 ? (
          <div className={styles.emptyState}>Sin inversiones registradas.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Inversionista</th>
                  <th>Monto</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {investments.map((inv) => {
                  return (
                    <tr key={`${inv.orderId}-${inv.userId}`}>
                      <td>
                        <div className={styles.cellMain}>{inv.userName}</div>
                        <div className={styles.cellSub}>{inv.userEmail || '—'}</div>
                      </td>
                      <td>
                        <div className={styles.cellMain}>{formatMoney(inv.amount, inv.currency)}</div>
                        <div className={styles.cellSub}>Orden #{inv.orderId}</div>
                      </td>
                      <td>
                        <div className={styles.cellMain}>{formatDateTime(inv.investedAt)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Retiros de inversiones cerradas</h2>
          <span className={styles.cardBadge}>{closures.length} retiros</span>
        </div>
        {closures.length === 0 ? (
          <div className={styles.emptyState}>Disponible cuando el proyecto esté finalizado.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Inversionista</th>
                  <th>Capital</th>
                  <th>Rendimiento</th>
                  <th>Total</th>
                  <th>Fecha cierre</th>
                </tr>
              </thead>
              <tbody>
                {closures.map((closure) => (
                  <tr key={`${closure.orderId}-${closure.userId}`}>
                    <td>
                      <div className={styles.cellMain}>{closure.userName}</div>
                      <div className={styles.cellSub}>{closure.userEmail || '—'}</div>
                    </td>
                    <td>
                      <div className={styles.cellMain}>{formatMoney(closure.capital, closure.currency)}</div>
                      <div className={styles.cellSub}>Orden #{closure.orderId}</div>
                    </td>
                    <td>
                      <div className={styles.cellMain}>{formatMoney(closure.yield, closure.currency)}</div>
                    </td>
                    <td>
                      <div className={styles.cellMain}>{formatMoney(closure.total, closure.currency)}</div>
                    </td>
                    <td>
                      <div className={styles.cellMain}>{formatDateTime(closure.closedAt)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Proyección</h2>
        </div>
        <div className={styles.projectionBody}>
          <div className={styles.projectionValue}>
            {data.projectedCloseDate
              ? `Cierre estimado: ${formatDate(data.projectedCloseDate)}`
              : 'Sin proyección.'}
          </div>
          <div className={styles.projectionNote}>{data.projectionNote || 'Estimación informativa.'}</div>
        </div>
      </section>
    </div>
  );
}
